import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSignupGate } from "@/context/SignupGate";
import { useDataSaver } from "@/context/DataSaverContext";
import {
  Heart, ChatCircle, ShareFat, Play, DotsThreeVertical, Prohibit,
  EnvelopeSimple, BookmarkSimple, Gift,
  PencilSimple, Trash, Check, X, DownloadSimple, MusicNote, Sparkle,
  Plus, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ReportButton from "@/components/ReportButton";
import { resolveMedia } from "@/lib/media";
import HlsVideo from "@/components/HlsVideo";

// Video sound is now permanently unmuted per Feb 2026 product change.
// The old per-user "muted" toggle in the options menu has been removed;
// autoplay policies mean the first video may fall back to muted-mute
// silently, and any user gesture (tap/scroll/keypress) instantly forces
// every currently-playing feed video back to sound-on.
// Global "audio unlock" flag. Starts false; flips to true on the FIRST user
// gesture anywhere on the page (Chrome/Safari's autoplay policy grants audio
// activation after a real user gesture). Once true, we NEVER globally-mute
// again, because any subsequent play() failure on an individual video is a
// per-video quirk (mid-flight abort, malformed source) and must not silence
// the rest of the feed.
let audioUnlocked = false;
let globalMuted = true;  // Pre-gesture: browsers won't let us play with sound anyway
const muteSubscribers = new Set();
function setGlobalMuted(v) {
  if (globalMuted === v) return;
  globalMuted = v;
  muteSubscribers.forEach((cb) => cb(v));
}

export const isFeedMuted = () => globalMuted;
export function subscribeFeedMute(cb) {
  muteSubscribers.add(cb);
  return () => muteSubscribers.delete(cb);
}
// Called from a real tap so iOS/Chrome engage audio inside the gesture.
export function unmuteFeedNow() {
  audioUnlocked = true;
  document.querySelectorAll('[data-testid="feed-container"] video').forEach((v) => {
    if (v.paused) return;
    try { v.muted = false; v.play().catch(() => {}); } catch { /* ignore */ }
  });
  setGlobalMuted(false);
}

// One-shot gesture listener: browsers only allow audio playback after a user
// gesture. The FIRST tap/click/scroll/keypress anywhere in the app permanently
// unlocks audio and flips every mounted feed video back to sound-on. From
// that moment forward, `audioUnlocked` stays true for the whole session so
// per-video autoplay hiccups can no longer cascade into a global mute.
//
// We listen on BOTH document and window in capture phase because:
//  - Mobile fast-scroll: `scroll` events don't bubble/capture across the DOM,
//    so an inner scroll container (Feed.jsx) doesn't reach a window scroll
//    listener. `touchstart` and `touchmove` bubble reliably though.
//  - `pointerdown` covers both mouse click and touch tap (desktop + mobile).
//  - `wheel` covers desktop trackpad/scroll-wheel fast scrolls.
if (typeof window !== "undefined" && !window.__tainUnmuteHookInstalled) {
  window.__tainUnmuteHookInstalled = true;
  const unlock = () => {
    audioUnlocked = true;
    if (globalMuted) setGlobalMuted(false);
  };
  const opts = { passive: true, capture: true };
  ["pointerdown", "touchstart", "touchmove", "wheel", "scroll", "click"].forEach((evt) => {
    document.addEventListener(evt, unlock, opts);
    window.addEventListener(evt, unlock, opts);
  });
  window.addEventListener("keydown", unlock, true);
}

const TIP_PRESETS = [10, 25, 50, 100]; // Points

export default function FeedItem({ post, onUpdate, index = 0 }) {
  const navigate = useNavigate();
  const { user: currentUser, refresh: refreshUser } = useAuth();
  const { requireAuth, showGate } = useSignupGate();
  const isGuest = !currentUser;
  const { enabled: dataSaver } = useDataSaver();
  const [liked, setLiked] = useState(!!post.is_liked);
  const [likeCount, setLikeCount] = useState(post.like_count);
  const [shareCount, setShareCount] = useState(post.share_count || 0);
  const [bookmarked, setBookmarked] = useState(!!post.is_bookmarked);
  const [tipTotal, setTipTotal] = useState(post.tip_total_points || post.tip_total || 0);
  const [following, setFollowing] = useState(!!post.is_following_author);
  const [commentOpen, setCommentOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [customTip, setCustomTip] = useState("");
  const [tipping, setTipping] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  const [newComment, setNewComment] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [hidden, setHidden] = useState(false);
  const [muted, setMuted] = useState(globalMuted);
  const [showPlayIcon, setShowPlayIcon] = useState(false);
  const [likeAnim, setLikeAnim] = useState(false);
  const [heartBursts, setHeartBursts] = useState([]);
  // Long-form: track the video's natural aspect ratio so the stage can size
  // to the SOURCE dimensions (letterbox/pillarbox as needed) instead of
  // forcing every clip into 9:16 like the previous short-form design.
  // Default 16/9 covers most long-form videos until metadata resolves.
  const [aspectRatio, setAspectRatio] = useState(
    post.aspect_ratio ||
    (post.width && post.height ? post.width / post.height : 16 / 9)
  );
  const videoRef = useRef(null);
  const soundRef = useRef(null);  // Overlay sound (voiceover or picked sound), synced with video
  const containerRef = useRef(null);
  const lastTapRef = useRef(0);
  // Track the most recent play() Promise so we can safely no-op pause() calls
  // that arrive mid-flight (fast-scroll case). Interrupting a pending play()
  // with pause() throws AbortError which used to cascade into globalMuted.
  const playPromiseRef = useRef(null);
  // §16 — photo/text credit-lock enforcement. Fires /qualify after the
  // content is visible past its threshold; on 402 renders a blocking
  // overlay with the "Buy Credits from Tainapp on Whatsapp" CTA (§17).
  const [creditLock, setCreditLock] = useState(null);   // {cost, remaining, admin_whatsapp}
  const qualifiedRef = useRef(false);

  // Sync overlay sound with the video: play/pause/seek together, respect global mute.
  useEffect(() => {
    const v = videoRef.current, a = soundRef.current;
    if (!v || !a) return;
    const onPlay = () => { a.currentTime = v.currentTime; a.play().catch(() => {}); };
    const onPause = () => a.pause();
    const onSeek = () => { a.currentTime = v.currentTime; };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("seeked", onSeek);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("seeked", onSeek);
    };
  }, [post.sound_audio_url]);

  // Iter-91: landscape-backdrop sync removed. Videos are transcoded to
  // full-bleed 9:16 via Qencode Smart Cropping so there's no letterbox
  // area to fill with a blurred copy anymore.

  useEffect(() => {
    if (soundRef.current) soundRef.current.muted = muted;
  }, [muted, post.sound_audio_url]);

  // Sync mute state across all FeedItems. Guard the callback against
  // firing after unmount (the muteSubscribers Set is module-level, so a
  // stale cleanup order could otherwise invoke setMuted on a dead React
  // fiber → its downstream player.muted(v) throws on a disposed tech).
  useEffect(() => {
    let mounted = true;
    const cb = (v) => { if (mounted) setMuted(v); };
    muteSubscribers.add(cb);
    return () => {
      mounted = false;
      muteSubscribers.delete(cb);
    };
  }, []);

  // Guests follow the SAME server-side watch threshold as signed-in users.
  // Once past the admin-configured qualification threshold, `/qualify`
  // returns HTTP 402 with `signup_required:true`; HlsVideo + the photo/text
  // qualify path both catch that and fire the SignupGate modal directly.
  // No session-wide client-side cap.

  // Play only when in view (>=60% visible), pause otherwise.
  // Also drives an adaptive preload strategy to slash bandwidth:
  //   - far from viewport: preload="none" (no download)
  //   - near viewport (< 2 screens away): preload="metadata"
  //   - fully in view: preload="auto" + play (buffers aggressively to prevent freezes)
  useEffect(() => {
    if (post.content_type !== "video" || !containerRef.current) return;
    const el = containerRef.current;

    // Observer for the "near viewport" hint — enables light preload well
    // ahead of visibility so fast-scrollers still see a sharp first frame.
    const nearObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const v = videoRef.current;
          if (!v) return;
          if (entry.isIntersecting) {
            if (v.preload === "none") v.preload = "metadata";
          } else {
            // Aggressively free memory/bandwidth for off-screen videos.
            if (v.preload !== "none") v.preload = "none";
          }
        });
      },
      { rootMargin: "200% 0px" }  // Preload two viewports ahead — smooths fast scrolls
    );

    // Safely await any pending play() before pausing. Interrupting a play
    // Promise with pause() throws AbortError which used to cascade into
    // globalMuted, killing sound for the entire feed after fast scrolls.
    const safePause = async (v) => {
      try {
        if (playPromiseRef.current) {
          await playPromiseRef.current.catch(() => {});
          playPromiseRef.current = null;
        }
        v.pause();
      } catch { /* ignore */ }
    };

    // Observer for actual playback
    const playObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const v = videoRef.current;
          if (!v) return;
          if (entry.intersectionRatio >= 0.6) {
            if (dataSaver) {
              // In Data Saver mode, do NOT autoplay — user taps to play. Just show the poster.
              setShowPlayIcon(true);
              return;
            }
            v.preload = "auto";
            // Attempt playback honoring the current global audio state. If
            // audio was already unlocked by a prior gesture, we play with
            // sound; otherwise we start muted and rely on the very next
            // user gesture to unlock everyone in one shot.
            v.muted = globalMuted;
            const p = v.play();
            if (p && typeof p.then === "function") {
              playPromiseRef.current = p;
              p.then(() => { playPromiseRef.current = null; })
               .catch((err) => {
                 playPromiseRef.current = null;
                 // NotAllowedError = autoplay policy denial. Fall back to
                 // muted ONLY on this individual video — never cascade a
                 // global mute (that killed sound across the entire feed
                 // whenever a new video hit the autoplay wall).
                 //
                 // AbortError (pause interrupted play during fast scroll)
                 // and any other rejection are silently ignored.
                 if (err && err.name === "NotAllowedError") {
                   // Only broadcast a global mute if the user hasn't yet
                   // performed any gesture at all. After the first gesture
                   // audio stays unlocked for the rest of the session.
                   if (!audioUnlocked) setGlobalMuted(true);
                   if (!v.muted) {
                     v.muted = true;
                     v.play().catch(() => {});
                   }
                 }
               });
            }
          } else {
            safePause(v);
            // Keep the current buffer but stop downloading more
            if (v.preload === "auto") v.preload = "metadata";
          }
        });
      },
      { threshold: [0, 0.6, 1] }
    );

    nearObserver.observe(el);
    playObserver.observe(el);
    return () => {
      nearObserver.disconnect();
      playObserver.disconnect();
    };
  }, [post.content_type, post.post_id, dataSaver]);

  // §16 — Photo & text credit-lock. Start a dwell timer when the card is
  // >=60% visible; when the timer crosses the server's configured threshold
  // (photo_qualification_seconds / text_qualification_seconds) we call
  // /qualify. On 402, we render a blocking overlay with the admin-WhatsApp
  // CTA. Videos have their own qualification path inside HlsVideo.jsx.
  useEffect(() => {
    if (post.content_type !== "image" && post.content_type !== "text") return;
    if (!containerRef.current) return;
    const el = containerRef.current;
    let dwellStart = 0;
    let totalMs = 0;
    let timer = null;
    let done = false;

    const stopTimer = () => {
      if (timer) { clearInterval(timer); timer = null; }
      if (dwellStart) {
        totalMs += Date.now() - dwellStart;
        dwellStart = 0;
      }
    };
    const tryQualify = async () => {
      if (done || qualifiedRef.current) return;
      const watched = (totalMs + (dwellStart ? Date.now() - dwellStart : 0)) / 1000;
      // Give the server enough dwell to definitely be past its threshold —
      // we start attempting at 5s and stop when it says qualified (or 402).
      if (watched < 5) return;
      try {
        const url = new URL(window.location.href);
        const ref = url.searchParams.get("ref") || undefined;
        const { data } = await api.post(`/posts/${post.post_id}/qualify`, {
          watched_seconds: watched, ref,
        });
        if (data?.qualified) { qualifiedRef.current = true; done = true; stopTimer(); }
      } catch (err) {
        if (err?.response?.status === 402) {
          done = true; stopTimer();
          const d = err.response.data?.detail || {};
          // Guest branch: server signals signup_required — fire the
          // SignupGate directly. Signed-in users flow through credit-lock.
          if (d.signup_required) {
            showGate("watchLimit");
            return;
          }
          const lockPayload = {
            cost: Number(d.cost || 0),
            remaining: Number(d.remaining || 0),
            admin_whatsapp: d.admin_whatsapp || "",
          };
          setCreditLock(lockPayload);
          // Also globally lock the feed so continuous scrolling is
          // disallowed until the user tops up (§17).
          try {
            window.dispatchEvent(new CustomEvent("tain:credit-lock", { detail: lockPayload }));
          } catch { /* ignore */ }
        }
      }
    };

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const visible = entry.intersectionRatio >= 0.6;
        if (visible) {
          if (!dwellStart) dwellStart = Date.now();
          if (!timer) timer = setInterval(tryQualify, 2000);
        } else {
          stopTimer();
        }
      });
    }, { threshold: [0, 0.6, 1] });
    io.observe(el);
    return () => { io.disconnect(); stopTimer(); };
  }, [post.content_type, post.post_id]);

  // Reset the lock and qualification state when the visible post changes
  // — otherwise a paused-and-swapped card would keep the previous lock.
  useEffect(() => {
    qualifiedRef.current = false;
    setCreditLock(null);
  }, [post.post_id]);

  // Keep video element mute state in sync (redundant with HlsVideo's own
  // player.muted() effect, but ensures the raw <video> element's muted
  // attribute matches the React state even if Video.js's tech is torn down).
  //
  // iOS Safari quirk: setting `.muted = false` on a currently-playing video
  // that was previously muted does NOT engage the audio track — the browser
  // requires a fresh `play()` call to fire audio decoding after the mute
  // state transitions to unmuted. Without this, videos autoplay silently
  // even after the user gesture unlocks audio globally.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    try { v.muted = muted; } catch { /* ignore */ }
    // If we just transitioned to unmuted while the video is in-view and
    // playing, ping play() to force the audio track to engage.
    if (!muted && !v.paused) {
      try { v.play().catch(() => {}); } catch { /* ignore */ }
    }
  }, [muted]);

  // Auto-recovery: if the player throws a media error or stalls for > 5s while it should be
  // playing, reload the source and try again. Uses exponential backoff up to 3 attempts.
  useEffect(() => {
    if (post.content_type !== "video") return;
    const v = videoRef.current;
    if (!v) return;
    let attempts = 0;
    let stallTimer = null;

    const clearStallTimer = () => {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };

    const recover = () => {
      if (attempts >= 3) {
        clearStallTimer();
        return;
      }
      attempts += 1;
      const t = v.currentTime;
      try {
        v.pause();
      } catch { /* ignore */ }
      // Reload src, restore playhead, resume
      v.load();
      const resume = () => {
        try { v.currentTime = t; } catch { /* ignore */ }
        v.play().catch(() => {});
        v.removeEventListener("loadedmetadata", resume);
      };
      v.addEventListener("loadedmetadata", resume);
    };

    const onError = () => {
      // Any media error: try to recover after a short delay
      setTimeout(recover, 400 * attempts);
    };
    const onStalled = () => {
      // Give the network a chance to recover naturally; only reload if still stalled
      clearStallTimer();
      stallTimer = setTimeout(() => {
        // If we haven't advanced and we should be playing, reload
        if (!v.paused && v.readyState < 3) recover();
      }, 5000);
    };
    const onPlaying = () => {
      // Success — reset attempt count and clear pending recovery
      attempts = 0;
      clearStallTimer();
    };

    v.addEventListener("error", onError);
    v.addEventListener("stalled", onStalled);
    v.addEventListener("waiting", onStalled);
    v.addEventListener("playing", onPlaying);
    return () => {
      v.removeEventListener("error", onError);
      v.removeEventListener("stalled", onStalled);
      v.removeEventListener("waiting", onStalled);
      v.removeEventListener("playing", onPlaying);
      clearStallTimer();
    };
  }, [post.content_type, post.post_id]);

  const togglePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      setShowPlayIcon(false);
    } else {
      v.pause();
      setShowPlayIcon(true);
    }
  };

  const doLike = useCallback(async (forceLike = false) => {
    if (!requireAuth(!isGuest, "like")) return;
    try {
      const { data } = await api.post(`/posts/${post.post_id}/like`);
      if (forceLike && !data.liked) {
        await api.post(`/posts/${post.post_id}/like`);
        setLiked(true);
        setLikeCount((c) => c + 1);
      } else {
        setLiked(data.liked);
        setLikeCount((c) => c + (data.liked ? 1 : -1));
        if (data.liked) {
          setLikeAnim(true);
          setTimeout(() => setLikeAnim(false), 400);
        }
      }
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Not enough Credits. Redirecting to Buy Credits…");
        setTimeout(() => navigate("/buy-points"), 800);
      } else {
        toast.error("Login required");
      }
    }
  }, [post.post_id, navigate, isGuest, requireAuth]);

  const spawnHeartBurst = (x, y) => {
    const id = Math.random().toString(36).slice(2);
    setHeartBursts((b) => [...b, { id, x, y }]);
    setTimeout(() => setHeartBursts((b) => b.filter((h) => h.id !== id)), 900);
  };

  // Single-tap: play/pause. Double-tap: like + heart burst
  const onTapMedia = (e) => {
    // Ignore taps that started on an interactive element
    const now = Date.now();
    const rect = containerRef.current?.getBoundingClientRect();
    const x = rect ? e.clientX - rect.left : 0;
    const y = rect ? e.clientY - rect.top : 0;
    if (now - lastTapRef.current < 320) {
      // Double-tap
      lastTapRef.current = 0;
      spawnHeartBurst(x, y);
      if (!liked) {
        setLiked(true);
        setLikeCount((c) => c + 1);
        doLike(true);
      } else {
        // Extra confirmation burst but no state flip on double-tap when already liked
      }
      return;
    }
    lastTapRef.current = now;
    // Delay single-tap action to allow double-tap to override
    setTimeout(() => {
      if (lastTapRef.current !== 0 && Date.now() - lastTapRef.current >= 320) {
        if (post.content_type === "video") {
          togglePlayPause();
        }
        lastTapRef.current = 0;
      }
    }, 330);
  };

  const blockAuthor = async () => {
    if (!post.author?.user_id) return;
    if (!window.confirm(`Block @${post.author.name}? Their posts won't appear in your feed.`)) return;
    try {
      await api.post(`/users/${post.author.user_id}/block`);
      toast.success(`Blocked @${post.author.name}`);
      setHidden(true);
      onUpdate?.();
    } catch {
      toast.error("Failed to block");
    }
  };

  const toggleLike = () => doLike(false);

  const sharePost = async () => {
    if (!requireAuth(!isGuest, "share")) return;
    try {
      const { data } = await api.post(`/posts/${post.post_id}/share`);
      setShareCount((c) => c + 1);
      const backend = process.env.REACT_APP_BACKEND_URL;
      const url = backend
        ? `${backend.replace(/\/$/, "")}${data.share_url}`
        : `${window.location.origin}${data.share_url}`;
      const shareText = post.caption ? `${post.caption.slice(0, 80)} — via Tain` : "Check this out on Tain";
      if (navigator.share) {
        try { await navigator.share({ title: "Tain", text: shareText, url }); } catch { /* cancelled */ }
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied!");
      }
      if (data.rewarded) toast.success("Reward credited to creator");
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Not enough Credits to share");
        setTimeout(() => navigate("/buy-points"), 800);
      } else {
        toast.error("Failed to share");
      }
    }
  };

  const toggleBookmark = async () => {
    if (!requireAuth(!isGuest, "save")) return;
    try {
      const { data } = await api.post(`/posts/${post.post_id}/bookmark`);
      setBookmarked(!!data.bookmarked);
      toast.success(data.bookmarked ? "Saved" : "Unsaved");
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Not enough Credits to save");
        setTimeout(() => navigate("/buy-points"), 800);
      } else {
        toast.error(e.response?.data?.detail || "Failed");
      }
    }
  };

  const deleteOwnPost = async () => {
    if (!window.confirm("Delete this post permanently? It will be removed from the feed and your profile immediately.")) return;
    try {
      await api.delete(`/posts/${post.post_id}`);
      toast.success("Post deleted");
      setHidden(true);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const downloadPost = async () => {
    if (!requireAuth(!isGuest, "download")) return;
    try {
      const { data } = await api.post(`/posts/${post.post_id}/download`);
      if (!data.media_url) {
        toast.error("No media to download");
        return;
      }
      const url = data.media_url.startsWith("http")
        ? data.media_url
        : `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.media_url}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = "";
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Downloading — TAIN watermark will appear on the file.");
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Not enough Credits to download");
        setTimeout(() => navigate("/buy-points"), 800);
      } else {
        toast.error(e.response?.data?.detail || "Failed");
      }
    }
  };

  const useSound = async () => {
    if (post.content_type !== "video") return;
    if (!requireAuth(!isGuest, "sound")) return;
    try {
      const { data } = await api.post(`/posts/${post.post_id}/use-sound`);
      if (data.already_saved) {
        toast.info("Already in your sound library");
      } else {
        toast.success("Sound saved to your library — use it in the editor when posting.");
      }
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Not enough Credits to reuse this sound");
        setTimeout(() => navigate("/buy-points"), 800);
      } else {
        toast.error(e.response?.data?.detail || "Failed");
      }
    }
  };

  const sendTip = async (points) => {
    if (!post.author?.user_id) return;
    if (!requireAuth(!isGuest, "wallet")) return;
    if (post.author.user_id === currentUser?.user_id) {
      toast.error("You cannot tip your own post");
      return;
    }
    const pts = Math.floor(Number(points));
    if (!pts || pts <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setTipping(true);
    try {
      await api.post(`/posts/${post.post_id}/tip`, { points: pts });
      setTipTotal((t) => t + pts);
      setTipOpen(false);
      setCustomTip("");
      toast.success(`Gifted ${pts} Credits — creator will receive ~40% as earnings.`);
      refreshUser?.();
      onUpdate?.();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Not enough Credits. Redirecting to Buy Credits…");
        setTimeout(() => navigate("/buy-points"), 800);
      } else {
        toast.error(e.response?.data?.detail || "Failed to gift");
      }
    } finally {
      setTipping(false);
    }
  };

  // Avatar badge tap:
  //   - Not following → instantly follow the creator (icon then swaps to Message)
  //   - Already following → jump straight to a direct chat with them
  const handleAvatarBadge = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!post.author?.user_id) return;
    if (!requireAuth(!isGuest, "follow")) return;
    if (following) {
      navigate(`/inbox?with=${post.author.user_id}`);
      return;
    }
    try {
      await api.post(`/users/${post.author.user_id}/follow`);
      setFollowing(true);
      toast.success(`Following @${post.author.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't follow — try again");
    }
  };

  const openComments = async () => {
    if (!requireAuth(!isGuest, "comment")) return;
    setCommentOpen(true);
    try {
      const { data } = await api.get(`/posts/${post.post_id}/comments`);
      setComments(data);
    } catch {
      // ignore
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    if (!requireAuth(!isGuest, "comment")) return;
    try {
      const { data } = await api.post(`/posts/${post.post_id}/comment`, { text: newComment });
      setComments((c) => [data.comment, ...c]);
      setNewComment("");
      // Update comment count locally so the feed does NOT trigger a full
      // reload — parent `onUpdate` used to refetch every item which reset
      // scroll position and yanked the current post out of view. Fixes
      // the "clicking Post jumps to a different video" bug (Feb 2026).
      setCommentCount((n) => n + 1);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Not enough Credits to comment");
        setTimeout(() => navigate("/buy-points"), 800);
      } else {
        toast.error(e.response?.data?.detail || "Failed to comment");
      }
    }
  };

  const startEditComment = (c) => {
    setEditingId(c.interaction_id);
    setEditText(c.text || "");
  };
  const cancelEditComment = () => { setEditingId(null); setEditText(""); };
  const saveEditComment = async () => {
    if (!editText.trim() || !editingId) return;
    try {
      await api.put(`/comments/${editingId}`, { text: editText });
      setComments((cs) =>
        cs.map((c) =>
          c.interaction_id === editingId ? { ...c, text: editText.trim(), edited_at: new Date().toISOString() } : c
        )
      );
      cancelEditComment();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to edit");
    }
  };
  const deleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${commentId}`);
      setComments((cs) => cs.filter((c) => c.interaction_id !== commentId));
      setCommentCount((n) => Math.max(0, n - 1));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div ref={containerRef} className="relative w-full h-full bg-black overflow-hidden select-none">
      {hidden && (
        <div className="absolute inset-0 z-20 bg-black/95 flex items-center justify-center">
          <p className="text-white/70 text-sm">Blocked. Scroll to next post.</p>
        </div>
      )}

      {/* §16-17 Credit lock overlay for photo & text (video has its own inside HlsVideo) */}
      {creditLock && post.content_type !== "video" && (
        <div
          data-testid="feed-credit-lock"
          onClick={(e) => e.stopPropagation()}
          className="absolute inset-0 z-30 flex items-center justify-center px-6"
          style={{
            background: "rgba(0,0,0,0.86)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          }}
        >
          <div className="max-w-sm text-center text-white space-y-3">
            <p className="font-heading text-xl font-black">You have run out of credits</p>
            <p className="text-sm text-white/80">
              To continue watching,{" "}
              {creditLock.admin_whatsapp ? (
                <a
                  href={creditLock.admin_whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="feed-credit-lock-admin-link"
                  className="text-emerald-400 font-bold underline"
                >
                  Buy Credits from Tainapp on Whatsapp
                </a>
              ) : (
                <span className="font-bold">Buy Credits from Tainapp on Whatsapp</span>
              )}
              .
            </p>
            {creditLock.cost > 0 && (
              <p className="text-[11px] text-white/50">
                This costs {creditLock.cost} credit{creditLock.cost === 1 ? "" : "s"} at
                the qualification threshold. You have {creditLock.remaining}.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Media */}
      {post.content_type === "video" && (post.hls_url || post.media_url) && (
        <>
          {/* Long-form video stage — preserves the source's true aspect
              ratio (letterbox/pillarbox as needed) instead of forcing
              every clip into a portrait rectangle. Blurred backdrop
              fills any letterbox area for visual cohesion. */}
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-black">
            {post.thumbnail_url && (
              <img
                src={resolveMedia(post.thumbnail_url)}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40 pointer-events-none"
              />
            )}
            <div
              className="relative bg-black overflow-hidden w-full h-full flex items-center justify-center"
              data-testid="feed-video-stage"
            >
              <div
                className="relative"
                style={{
                  aspectRatio: aspectRatio || (16 / 9),
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: aspectRatio && aspectRatio < 1 ? "auto" : "100%",
                  height: aspectRatio && aspectRatio < 1 ? "100%" : "auto",
                }}
              >
                <HlsVideo
                  ref={videoRef}
                  src={resolveMedia(post.hls_url || post.media_url)}
                  poster={post.thumbnail_url ? resolveMedia(post.thumbnail_url) : undefined}
                  className="absolute inset-0 w-full h-full cursor-pointer"
                  loop
                  muted={muted}
                  playsInline
                  /* Aggressive preload for the first 3 items so the top of
                     the feed paints and starts playing instantly on load.
                     Deeper items keep 'metadata' + on-demand upgrade via
                     the IntersectionObserver preload strategy. */
                  preload={index < 3 ? "auto" : "metadata"}
                  autoplay={index === 0}
                  controls={false}
                  trackProgress={true}
                  postId={post.post_id}
                  onClick={onTapMedia}
                  onLoadedMetadata={(e) => {
                    const el = e?.currentTarget;
                    if (el && el.videoWidth && el.videoHeight) {
                      setAspectRatio(el.videoWidth / el.videoHeight);
                    }
                  }}
                />
                {showPlayIcon && (
                  <button
                    onClick={togglePlayPause}
                    className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
                  >
                    <div className="w-20 h-20 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center">
                      <Play size={40} weight="fill" className="text-white ml-1" />
                    </div>
                  </button>
                )}
                {/* Seekable progress bar — tap or drag to jump. */}
                <VideoSeekBar videoRef={videoRef} postId={post.post_id} />
              </div>
            </div>
          </div>
          {post.sound_audio_url && (
            <audio
              ref={soundRef}
              src={resolveMedia(post.sound_audio_url)}
              preload="auto"
              loop
              data-testid={`sound-audio-${post.post_id}`}
            />
          )}
        </>
      )}
      {post.content_type === "image" && post.media_url && (
        <div className="absolute inset-0 cursor-pointer" onClick={onTapMedia}>
          <img
            src={resolveMedia(post.media_url)}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40"
          />
          <img
            src={resolveMedia(post.media_url)}
            alt=""
            className="absolute inset-0 m-auto max-w-full max-h-[75vh] md:max-h-[85vh] object-contain px-3 md:px-8"
          />
        </div>
      )}
      {post.content_type === "text" && (
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-black to-emerald-900 flex items-center justify-center p-8 cursor-pointer" onClick={onTapMedia}>
          <p className="font-heading text-3xl font-bold text-white text-center leading-tight">
            {post.caption}
          </p>
        </div>
      )}

      {/* Double-tap heart bursts */}
      {heartBursts.map((h) => (
        <div
          key={h.id}
          className="pointer-events-none absolute z-20"
          style={{ left: h.x - 40, top: h.y - 40 }}
        >
          <Heart size={80} weight="fill" className="text-red-500 heart-burst" />
        </div>
      ))}

      {/* Bottom gradient + caption */}
      <div className="absolute bottom-0 left-0 right-0 p-4 pb-6 bg-gradient-to-t from-black/95 via-black/60 to-transparent pr-16">
        {post.is_pinned_tutorial && (
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/20 border border-blue-400/40 text-blue-200 text-[11px] font-heading font-black uppercase tracking-widest mb-1.5"
            data-testid={`tutorial-badge-${post.post_id}`}
          >
            <Sparkle size={12} weight="fill" />
            Tutorial · How Tain works
          </div>
        )}
        {post.content_type !== "text" && post.caption && (
          <p className="text-sm text-white/85 leading-snug line-clamp-3">{post.caption}</p>
        )}
        {(post.tags || []).length > 0 && (
          <p className="text-xs text-blue-300/80 mt-1 truncate">
            {(post.tags || []).slice(0, 4).map((t) => `#${t}`).join(" ")}
          </p>
        )}
      </div>

      {/* Right action rail — avatar sits at the very TOP with breathing room */}
      <div className="absolute right-2 top-16 bottom-4 flex flex-col items-center gap-3.5 z-10">
        {/* Author avatar + Follow/Message swap badge (badge sits ABOVE the avatar). */}
        {(() => {
          const authorId = post.author?.user_id;
          const isOwnPost = authorId && currentUser?.user_id && authorId === currentUser.user_id;
          const showBadge = !!authorId && !isOwnPost;
          return (
            <div className="relative pt-3">
              {showBadge && (
                <button
                  type="button"
                  data-testid={`avatar-follow-swap-${post.post_id}`}
                  aria-label={following ? `Message @${post.author?.name}` : `Follow @${post.author?.name}`}
                  onClick={handleAvatarBadge}
                  className={`absolute -top-1 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ring-2 ring-black transition-all active:scale-90 z-20 ${
                    following
                      ? "bg-gradient-to-br from-sky-400 to-blue-600 text-white"
                      : "bg-gradient-to-br from-rose-500 to-red-600 text-white"
                  }`}
                >
                  {following ? (
                    <PaperPlaneTilt size={12} weight="fill" data-testid={`avatar-message-icon-${post.post_id}`} />
                  ) : (
                    <Plus size={14} weight="bold" data-testid={`avatar-follow-icon-${post.post_id}`} />
                  )}
                </button>
              )}
              {post.author?.picture ? (
                <Link
                  data-testid={`author-avatar-${post.post_id}`}
                  to={authorId ? `/u/${authorId}` : "#"}
                  onClick={(e) => !authorId && e.preventDefault()}
                >
                  <img
                    src={post.author.picture}
                    alt=""
                    className="w-11 h-11 rounded-full border-2 border-white/40 hover:ring-2 hover:ring-blue-400/60 transition"
                  />
                </Link>
              ) : (
                <div className="w-11 h-11 rounded-full bg-white/10" />
              )}
            </div>
          );
        })()}

        <button
          data-testid={`like-btn-${post.post_id}`}
          onClick={toggleLike}
          className="group flex flex-col items-center gap-0.5 active:scale-95 transition-transform"
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-200 ring-1 ${
            liked
              ? "bg-red-500/25 ring-red-400/70 shadow-[0_0_20px_-4px_rgba(239,68,68,0.7)]"
              : "bg-white/10 ring-white/15 group-hover:bg-white/15"
          }`}>
            <Heart
              size={22}
              weight={liked ? "fill" : "bold"}
              className={`${liked ? "text-red-400" : "text-white"} ${likeAnim ? "animate-ping-slow" : ""}`}
            />
          </div>
          <span className="text-[11px] font-bold text-white drop-shadow">{likeCount}</span>
        </button>

        <button
          data-testid={`comment-btn-${post.post_id}`}
          onClick={openComments}
          className="group flex flex-col items-center gap-0.5 active:scale-95 transition-transform"
        >
          <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md ring-1 ring-white/15 flex items-center justify-center group-hover:bg-blue-500/20 group-hover:ring-blue-400/50 transition-all duration-200">
            <ChatCircle size={22} weight="bold" className="text-white group-hover:text-blue-300" />
          </div>
          <span className="text-[11px] font-bold text-white drop-shadow">{commentCount}</span>
        </button>

        <button
          data-testid={`bookmark-btn-${post.post_id}`}
          onClick={toggleBookmark}
          className="group flex flex-col items-center gap-0.5 active:scale-95 transition-transform"
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md ring-1 transition-all duration-200 ${
            bookmarked ? "bg-yellow-500/25 ring-yellow-300/60" : "bg-white/10 ring-white/15 group-hover:bg-white/15"
          }`}>
            <BookmarkSimple size={22} weight={bookmarked ? "fill" : "bold"} className={bookmarked ? "text-yellow-300" : "text-white"} />
          </div>
          <span className="text-[11px] font-bold text-white drop-shadow">{bookmarked ? "Saved" : "Save"}</span>
        </button>

        <button
          data-testid={`gift-btn-${post.post_id}`}
          onClick={() => { if (requireAuth(!isGuest, "wallet")) setTipOpen(true); }}
          className="group flex flex-col items-center gap-0.5 active:scale-95 transition-transform"
        >
          <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md ring-1 ring-white/15 flex items-center justify-center group-hover:bg-fuchsia-500/25 group-hover:ring-fuchsia-400/60 transition-all duration-200">
            <Gift size={22} weight="bold" className="text-white group-hover:text-fuchsia-300" />
          </div>
          <span className="text-[11px] font-bold text-white drop-shadow">{tipTotal > 0 ? `${tipTotal}pt` : "Gift"}</span>
        </button>

        <button
          data-testid={`share-btn-${post.post_id}`}
          onClick={sharePost}
          className="group flex flex-col items-center gap-0.5 active:scale-95 transition-transform"
        >
          <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md ring-1 ring-white/15 flex items-center justify-center group-hover:bg-emerald-500/25 group-hover:ring-emerald-400/60 transition-all duration-200">
            <ShareFat size={20} weight="fill" className="text-white group-hover:text-emerald-300" />
          </div>
          <span className="text-[11px] font-bold text-white drop-shadow">{shareCount}</span>
        </button>

        <ReportButton targetType="post" targetId={post.post_id} />

        {/* Options menu (mute, message author, download, block, delete). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={`post-menu-${post.post_id}`}
              className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md ring-1 ring-white/15 flex items-center justify-center hover:bg-white/15"
              aria-label="Post options"
            >
              <DotsThreeVertical size={22} weight="bold" className="text-white" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#121212] border-white/10 text-white">
            {post.author?.user_id && (
              <DropdownMenuItem
                data-testid={`message-author-${post.post_id}`}
                onClick={() => navigate(`/inbox?with=${post.author.user_id}`)}
                className="cursor-pointer"
              >
                <EnvelopeSimple size={16} weight="bold" className="mr-2" />
                Message @{post.author?.name || "user"}
              </DropdownMenuItem>
            )}
            {post.content_type !== "text" && (
              <DropdownMenuItem
                data-testid={`download-${post.post_id}`}
                onClick={downloadPost}
                className="cursor-pointer"
              >
                <DownloadSimple size={16} weight="bold" className="mr-2" />
                Download (2 cr) — with watermark
              </DropdownMenuItem>
            )}
            {post.content_type === "video" && (
              <DropdownMenuItem
                data-testid={`use-sound-${post.post_id}`}
                onClick={useSound}
                className="cursor-pointer"
              >
                <MusicNote size={16} weight="bold" className="mr-2" />
                Use this sound (1 pt)
              </DropdownMenuItem>
            )}
            {currentUser && post.author?.user_id === currentUser.user_id && (
              <DropdownMenuItem
                data-testid={`delete-own-${post.post_id}`}
                onClick={deleteOwnPost}
                className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
              >
                <Trash size={16} weight="bold" className="mr-2" />
                Delete this post
              </DropdownMenuItem>
            )}
            {(!currentUser || post.author?.user_id !== currentUser.user_id) && (
              <DropdownMenuItem
                data-testid={`block-author-${post.post_id}`}
                onClick={blockAuthor}
                className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
              >
                <Prohibit size={16} weight="bold" className="mr-2" />
                Block @{post.author?.name || "user"}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Tip / Gift dialog */}
      <Dialog open={tipOpen} onOpenChange={setTipOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Gift size={20} weight="fill" className="text-fuchsia-400" />
              Gift @{post.author?.name || "user"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
              <p className="text-xs uppercase tracking-widest text-white/50 font-bold">Your Credits</p>
              <p className="font-heading text-2xl font-black text-yellow-400">{currentUser?.points ?? 0}</p>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {TIP_PRESETS.map((pts) => (
                <button
                  key={pts}
                  data-testid={`tip-preset-${pts}`}
                  disabled={tipping}
                  onClick={() => sendTip(pts)}
                  className="py-3 rounded-lg bg-gradient-to-br from-fuchsia-500/20 to-pink-500/20 ring-1 ring-fuchsia-400/30 hover:brightness-125 disabled:opacity-50 font-heading font-black"
                >
                  {pts}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="tip-custom-input"
                type="number"
                min="1"
                step="1"
                value={customTip}
                onChange={(e) => setCustomTip(e.target.value)}
                placeholder="Custom Credits"
                className="bg-white/5 border-white/10 text-white"
              />
              <Button
                data-testid="tip-custom-send"
                disabled={tipping || !customTip}
                onClick={() => sendTip(parseInt(customTip, 10))}
                className="bg-fuchsia-600 hover:bg-fuchsia-700 font-heading font-bold"
              >
                Send
              </Button>
            </div>
            <p className="text-[11px] text-white/40 text-center">
              Gifts are paid in Credits. Creator receives 40% as cash earnings.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comments dialog — mobile-optimised so the input stays above the
          on-screen keyboard. Anchored near the top on phones (translate-y-0)
          and reverts to shadcn's centered layout on ≥sm. */}
      <Dialog open={commentOpen} onOpenChange={setCommentOpen}>
        <DialogContent
          className="bg-[#121212] border-white/10 text-white max-w-md
                     top-[6%] translate-y-0 max-h-[calc(100vh-8rem)] overflow-hidden
                     sm:top-[50%] sm:translate-y-[-50%] sm:max-h-none flex flex-col"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Comments</DialogTitle>
          </DialogHeader>
          {/* Composer moved TO THE TOP on mobile so it's always visible
              directly under the header when the keyboard slides up. */}
          <div className="sm:order-2 order-1 flex gap-2 sm:pt-3 sm:border-t border-white/10">
            <Input
              data-testid={`comment-input-${post.post_id}`}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitComment()}
              placeholder="Add a comment…"
              className="bg-white/5 border-white/10 text-white"
            />
            <Button data-testid={`comment-send-${post.post_id}`} onClick={submitComment} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
              Post
            </Button>
          </div>
          <div className="sm:order-1 order-2 flex-1 overflow-y-auto space-y-3 pr-1 min-h-0
                          max-h-[45vh] sm:max-h-[50vh]">
            {comments.length === 0 ? (
              <p className="text-white/50 text-sm text-center py-8">No comments yet. Be first.</p>
            ) : (
              comments.map((c) => {
                const isMine = currentUser && c.user_id === currentUser.user_id;
                const isEditing = editingId === c.interaction_id;
                return (
                  <div key={c.interaction_id} className="flex gap-2 group">
                    {c.author?.picture ? (
                      <img src={c.author.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-white/10" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold">{c.author?.name || "user"}</p>
                        {c.edited_at && <span className="text-[10px] text-white/40">(edited)</span>}
                      </div>
                      {isEditing ? (
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            data-testid={`comment-edit-input-${c.interaction_id}`}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && saveEditComment()}
                            className="bg-white/5 border-white/10 text-white h-8 text-sm"
                          />
                          <button
                            data-testid={`comment-edit-save-${c.interaction_id}`}
                            onClick={saveEditComment}
                            className="p-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700"
                            aria-label="Save"
                          >
                            <Check size={14} weight="bold" className="text-white" />
                          </button>
                          <button
                            data-testid={`comment-edit-cancel-${c.interaction_id}`}
                            onClick={cancelEditComment}
                            className="p-1.5 rounded-md bg-white/10 hover:bg-white/20"
                            aria-label="Cancel"
                          >
                            <X size={14} weight="bold" className="text-white" />
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-white/80 break-words">{c.text}</p>
                      )}
                    </div>
                    {isMine && !isEditing && (
                      <div className="flex items-start gap-1 opacity-70 group-hover:opacity-100">
                        <button
                          data-testid={`comment-edit-${c.interaction_id}`}
                          onClick={() => startEditComment(c)}
                          className="p-1 rounded hover:bg-white/10 text-white/70"
                          aria-label="Edit comment"
                        >
                          <PencilSimple size={14} weight="bold" />
                        </button>
                        <button
                          data-testid={`comment-delete-${c.interaction_id}`}
                          onClick={() => deleteComment(c.interaction_id)}
                          className="p-1 rounded hover:bg-red-500/20 text-red-400"
                          aria-label="Delete comment"
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Seekable progress bar rendered under each feed video.
 * Tap or drag anywhere along the line to jump playback to that timeframe. */
function VideoSeekBar({ videoRef, postId }) {
  const [progress, setProgress] = useState(0);          // 0..1
  const [dragging, setDragging] = useState(false);
  const barRef = useRef(null);

  // Poll playback progress. Cheaper than binding to every timeupdate event.
  useEffect(() => {
    let raf;
    const tick = () => {
      const v = videoRef?.current;
      if (v && !dragging && v.duration > 0 && !isNaN(v.duration)) {
        setProgress(Math.min(1, v.currentTime / v.duration));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [videoRef, dragging]);

  const seekTo = (clientX) => {
    const bar = barRef.current;
    const v = videoRef?.current;
    if (!bar || !v || !v.duration || isNaN(v.duration)) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setProgress(ratio);
    v.currentTime = ratio * v.duration;
  };

  const onDown = (e) => {
    e.stopPropagation();
    setDragging(true);
    seekTo(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onMove = (e) => {
    if (!dragging) return;
    seekTo(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onUp = () => setDragging(false);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  return (
    <div
      ref={barRef}
      data-testid={`video-seek-bar-${postId}`}
      onMouseDown={onDown}
      onTouchStart={onDown}
      className="absolute bottom-0 left-0 right-0 h-3 flex items-end px-1.5 pb-1 z-20 cursor-pointer group"
    >
      <div className="relative w-full h-[3px] rounded-full bg-white/20 group-hover:h-[5px] transition-[height]">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-white/90"
          style={{ width: `${progress * 100}%` }}
        />
        {dragging && (
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow"
            style={{ left: `calc(${progress * 100}% - 6px)` }}
          />
        )}
      </div>
    </div>
  );
}

